import {
  Button,
  ChakraProps,
  CircularProgress,
  FormControl,
  FormErrorMessage,
  Input,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiChevronDown } from 'react-icons/bi';
import { CategoryInList, GlobalRoles } from 'src/api/v1-api';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useAuth } from 'src/utils/auth';
import useRequest from 'src/utils/hooks/useRequest';

export type CloneCategoryMenuButtonProps = { categoryId?: string | number } & ChakraProps;

export type CloneCategoryFields = {
  subdomain: string;
};

const CloneCategoryMenuButton = ({ categoryId, ...props }: CloneCategoryMenuButtonProps) => {
  // Hooks
  const { control, handleSubmit } = useForm<CloneCategoryFields>({
    defaultValues: {
      subdomain: '',
    },
  });
  const { post } = useRequest();
  const { marketplace } = useMarketplaceContext();
  const toast = useToast();
  const { hasRole } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Local properties
  const userHasClonePermission = hasRole(marketplace?.id, GlobalRoles.Configuration);

  const onSubmit = async (data: CloneCategoryFields) => {
    try {
      setLoading(true);
      const res = await post<CategoryInList>({
        url: `/categories/${marketplace?.id}/${categoryId}/admin/clone/`,
        body: {
          slug: data.subdomain,
        },
      });

      if (res) {
        toast({
          title: 'Category has been cloned',
          isClosable: true,
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: 'No returned data from cloning',
          isClosable: true,
          status: 'warning',
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: 'There was an error while cloning category',
        isClosable: true,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  if (!userHasClonePermission) {
    return null;
  }

  return (
    <Popover
      placement="bottom"
      closeOnBlur={false}
      isOpen={open}
      onOpen={() => setOpen(true)}
      onClose={() => !loading && setOpen(false)}
      closeOnEsc
    >
      <PopoverTrigger>
        <Button alignSelf="flex-end" rightIcon={<BiChevronDown />} {...props}>
          Clone Category
        </Button>
      </PopoverTrigger>

      <PopoverContent>
        <PopoverBody p={6} position="relative">
          <PopoverCloseButton />
          {loading && (
            <Stack
              zIndex={100}
              bg="whiteAlpha.900"
              position="absolute"
              inset={0}
              alignItems="center"
              justifyContent="center"
              rounded={4}
            >
              <CircularProgress isIndeterminate />
            </Stack>
          )}
          <Stack as="form" onSubmit={handleSubmit(onSubmit)} gap={2}>
            <Text>Enter the marketplace subdomain</Text>

            <Controller
              name="subdomain"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Subdomain is required',
                },
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormControl id="subdomain" isInvalid={Boolean(error)}>
                    <Input {...field} />
                    {error?.message && <FormErrorMessage>{error.message}</FormErrorMessage>}
                  </FormControl>
                );
              }}
            />

            <Button type="submit" colorScheme="primary">
              Submit
            </Button>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default CloneCategoryMenuButton;
